import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Person } from '../../types/person.js';

@customElement('cp-person-list')
export class CpPersonList extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) people: Person[] = [];

  @state() private _searchQuery = '';
  @state() private _categoryFilter = '';

  private get _categories(): string[] {
    const cats = new Set(this.people.map(p => p.category).filter(Boolean));
    return Array.from(cats).sort();
  }

  private get _filteredPeople(): Person[] {
    let result = this.people;

    if (this._searchQuery.trim()) {
      const q = this._searchQuery.toLowerCase();
      result = result.filter(p =>
        p.full_name.toLowerCase().includes(q) ||
        (p.nickname?.toLowerCase().includes(q) ?? false)
      );
    }

    if (this._categoryFilter) {
      result = result.filter(p => p.category === this._categoryFilter);
    }

    return result;
  }

  private _getInitials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map(n => n.charAt(0).toUpperCase())
      .join('');
  }

  private _handleSearchInput(e: Event): void {
    this._searchQuery = (e.target as HTMLInputElement).value;
  }

  private _handleCategoryChange(e: Event): void {
    this._categoryFilter = (e.target as HTMLSelectElement).value;
  }

  private _handleClearSearch(): void {
    this._searchQuery = '';
    this._categoryFilter = '';
  }

  private _handlePersonClick(personId: number): void {
    this.dispatchEvent(new CustomEvent('person-selected', {
      detail: { personId },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    const filtered = this._filteredPeople;
    const hasFilter = this._searchQuery.trim() || this._categoryFilter;

    return html`
      <div class="cp-person-list">
        <div class="cp-person-list__filters">
          <div class="cp-person-list__search-wrapper">
            <input
              class="cp-person-list__search"
              type="text"
              placeholder="Nach Name suchen..."
              .value=${this._searchQuery}
              @input=${this._handleSearchInput}
            />
          </div>
          <select
            class="cp-person-list__category-filter"
            .value=${this._categoryFilter}
            @change=${this._handleCategoryChange}
          >
            <option value="">Alle Kategorien</option>
            ${this._categories.map(cat => html`
              <option value=${cat} ?selected=${this._categoryFilter === cat}>${cat}</option>
            `)}
          </select>
        </div>

        ${this._renderList(filtered, hasFilter)}
      </div>
    `;
  }

  private _renderList(filtered: Person[], hasFilter: boolean | string) {
    if (this.people.length === 0) {
      return html`
        <div class="cp-person-list__empty">
          <div class="cp-person-list__empty-icon">&#128101;</div>
          <p class="cp-person-list__empty-title">Noch keine Personen angelegt</p>
        </div>
      `;
    }

    if (filtered.length === 0) {
      return html`
        <div class="cp-person-list__empty">
          <div class="cp-person-list__empty-icon">&#128269;</div>
          <p class="cp-person-list__empty-title">Keine Personen gefunden</p>
          ${hasFilter ? html`
            <button class="cp-person-list__clear-btn" @click=${this._handleClearSearch}>
              Suche zuruecksetzen
            </button>
          ` : nothing}
        </div>
      `;
    }

    return html`
      <div class="cp-person-list__grid">
        ${filtered.map(person => this._renderCard(person))}
      </div>
    `;
  }

  private _renderCard(person: Person) {
    return html`
      <div
        class="cp-person-card"
        @click=${() => this._handlePersonClick(person.id)}
        role="button"
        tabindex="0"
        @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._handlePersonClick(person.id)}
      >
        <div class="cp-person-card__avatar">${this._getInitials(person.full_name)}</div>
        <div class="cp-person-card__info">
          <div class="cp-person-card__name">${person.full_name}</div>
          ${person.subtitle ? html`<div class="cp-person-card__subtitle">${person.subtitle}</div>` : nothing}
        </div>
        ${person.category ? html`
          <div class="cp-person-card__meta">
            <span class="cp-person-card__category">${person.category}</span>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-list': CpPersonList;
  }
}
